import { useEffect, useRef, useState, useCallback } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface DraftEnvelope<T> {
  data: T
  savedAt: string
}

interface UseAutoSaveOptions<T> {
  /** Clé localStorage unique (ex: "sermon-draft-new" ou "sermon-draft-{id}") */
  key: string
  /** Données à sauver */
  data: T
  /** Désactiver si le formulaire est vide */
  enabled?: boolean
  /** Debounce pour localStorage (ms). Default : 500 */
  debounceMs?: number
}

interface UseAutoSaveReturn {
  /** Date du dernier save localStorage */
  lastSaved: Date | null
}

// ─── Hook principal ───────────────────────────────────────────────────────────

/**
 * Auto-save dans localStorage avec debounce.
 * Save immédiat quand l'app passe en background (visibilitychange).
 */
export function useAutoSave<T>({
  key,
  data,
  enabled = true,
  debounceMs = 500,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn & { clear: () => void } {
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const dataRef = useRef(data)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const clearedRef = useRef(false)

  dataRef.current = data

  const saveNow = useCallback(() => {
    if (!enabled) return
    try {
      const envelope: DraftEnvelope<T> = {
        data: dataRef.current,
        savedAt: new Date().toISOString(),
      }
      localStorage.setItem(key, JSON.stringify(envelope))
      setLastSaved(new Date())
    } catch {
      // Quota exceeded ou autre — on ignore silencieusement
    }
  }, [key, enabled])

  // Save avec debounce à chaque changement de data
  useEffect(() => {
    if (!enabled) return

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(saveNow, debounceMs)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [data, saveNow, debounceMs, enabled])

  // Save immédiat quand l'app passe en background
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && enabled) saveNow()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [enabled, saveNow])

  // Save immédiat au unmount (navigation arrière, fermeture)
  useEffect(() => {
    return () => {
      if (enabled && !clearedRef.current) {
        try {
          const envelope: DraftEnvelope<T> = {
            data: dataRef.current,
            savedAt: new Date().toISOString(),
          }
          localStorage.setItem(key, JSON.stringify(envelope))
        } catch { /* ignore */ }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])

  const clear = useCallback(() => {
    clearedRef.current = true
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    clearDraft(key)
  }, [key])

  return { lastSaved, clear }
}

// ─── Restauration ─────────────────────────────────────────────────────────────

/**
 * Lit un brouillon depuis localStorage.
 * Retourne null si aucun brouillon ou si le parsing échoue.
 */
export function restoreDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const envelope: DraftEnvelope<T> = JSON.parse(raw)
    return envelope.data
  } catch {
    return null
  }
}

/**
 * Supprime un brouillon (après save réussi ou reset volontaire).
 */
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch { /* ignore */ }
}

// ─── Nettoyage des drafts orphelins ───────────────────────────────────────────

/**
 * Supprime les brouillons de plus de `maxAgeDays` jours.
 * À appeler une fois au mount de l'app.
 */
export function cleanOldDrafts(maxAgeDays = 7): void {
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (!key?.includes('-draft-')) continue
      try {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const envelope = JSON.parse(raw)
        if (new Date(envelope.savedAt).getTime() < cutoff) {
          localStorage.removeItem(key)
        }
      } catch {
        // Entrée invalide → supprimer
        if (key) localStorage.removeItem(key)
      }
    }
  } catch { /* ignore */ }
}
